/**
 * hrStdUse store - 작업 법인의 인사 기준 6종(ORG·JOB·TITLE·POS·GRADE·STEP) 사용 여부와 오늘 유효한 기준 수.
 * 내 정보(/api/auth/me) 응답으로 채운다. 사용 설정 조회 API 는 인사정보 관리 메뉴용이라 일반 화면은 이 값을 읽는다.
 * 기준을 숨기는 화면은 들어올 때 refresh 로 다시 받는다 - 다른 관리자·다른 탭에서 바꾼 설정도 보이게.
 */
import { ref } from 'vue';
import { defineStore } from 'pinia';
import { myProfileRepo } from '@/services/auth';

export const useHrStdUseStore = defineStore('hrStdUse', () => {
  const items = ref(null);   // [{ stdTypeCd, useYn, dataCnt }]. null = 아직 받지 않음(조회 실패 포함)
  let pending = null;        // 진행 중인 내 정보 조회. 레이아웃과 화면이 같이 기다린다
  let gen = 0;               // 비운 뒤 늦게 도착한 옛 응답을 쓰지 않게 센다

  // 사용 안 함으로 설정한 기준. 받지 못했으면 숨기지 않는다(조회 실패로 항목이 사라지지 않게)
  const isOff = (type) => !!items.value?.some((r) => r.stdTypeCd === type && r.useYn === 'N');
  // 오늘 유효한 기준 수. 받지 못했으면 null
  const dataCnt = (type) => items.value?.find((r) => r.stdTypeCd === type)?.dataCnt ?? null;

  // 응답에 목록이 없으면(옛 서버) 받지 않은 상태로 둔다
  function set(list) {
    items.value = Array.isArray(list) ? list : null;
  }

  // 이미 나간 내 정보 조회를 같이 쓴다. 실패는 기억하지 않아 다음 ensure 가 다시 부른다
  function track(meRequest) {
    const at = gen;
    const p = Promise.resolve(meRequest)
      .then((me) => { if (at === gen) set(me?.hrStdUse); return items.value; })
      .catch(() => items.value)
      .finally(() => { if (pending === p) pending = null; });
    pending = p;
    return p;
  }

  // 화면 진입용. 진행 중인 조회가 있으면 같이 쓰고, 실패하면 앞 값을 둔다
  function refresh() {
    return pending || track(myProfileRepo.me());
  }

  // 받아 둔 값이 있으면 그대로 쓴다
  async function ensure() {
    return items.value || refresh();
  }

  // 인사정보 관리에서 사용 설정이나 기준을 바꾼 뒤, 로그인-로그아웃 때 부른다.
  // 법인 복귀(auth.resetCompany)는 값만 비운다(set(null)) - 진행 중인 조회가 소속 법인으로 다시 보내져 맞는 값을 채운다
  // invalidate 로 바꾸면 그 응답을 버린다(회귀 테스트 「법인 복귀」)
  function invalidate() {
    gen += 1;
    pending = null;
    items.value = null;
  }

  return { items, isOff, dataCnt, set, track, refresh, ensure, invalidate };
});
